import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { updateOrgName } from '../utils/subscription';
import styles from './EditTeamNameModal.module.css';

interface EditTeamNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTeamNameUpdated: (newName: string) => void;
  currentName: string;
  orgId: string;
}

const EditTeamNameModal: React.FC<EditTeamNameModalProps> = ({
  isOpen,
  onClose,
  onTeamNameUpdated,
  currentName,
  orgId
}) => {
  const [newName, setNewName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      setError('Team name cannot be empty');
      return;
    }

    if (newName.trim() === currentName) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await updateOrgName(orgId, newName.trim());
      onTeamNameUpdated(newName.trim());
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update team name');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewName(currentName);
    setError(null);
    onClose();
  };

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Team Name</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className={styles.errorAlert}>
              {error}
            </Alert>
          )}
          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.formLabel}>Team Name</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className={styles.formControl}
              placeholder="Enter team name"
              autoFocus
            />
            <Form.Text className={styles.helpText}>
              Enter a new name for your team
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="secondary"
            onClick={handleClose}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className={styles.submitButton}
            disabled={isSubmitting || !newName.trim() || newName.trim() === currentName}
          >
            {isSubmitting ? 'Updating...' : 'Update Team Name'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditTeamNameModal; 